/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Fiche, FicheContainerContext } from '@mairistem/fiche';
import { request } from '@mairistem/tools';
import { formatNomClassement } from '@/utils';

import type { Pacs, Registre } from '@/types';
import { StatutPacs } from '@/constants';
import { getListeRegistres } from '@/routes';
import InformationPacsPanel from './Panels/InformationPacs/InformationsPacsPanel';
import InformationsDeclarationsPacsPanel from './Panels/InformationsDeclarations/InformationsDeclarationsPacsPanel';

const flattenRegistres = (nodes: any[]): any[] =>
  nodes.flatMap((node) =>
    node.identifiant != null
      ? [node]
      : flattenRegistres(node.children ?? node.items ?? []),
  );

interface PacsFicheProps {
  loading?: boolean;
  pacs?: Pacs;
}

const PacsFiche = (props: PacsFicheProps) => {
  const { loading, pacs: pacsProps } = props;

  const { service } = React.useContext(FicheContainerContext);
  const { update } = service || {};

  const [pacs, setPacs] = React.useState<Pacs>(pacsProps);

  // Convention demandée dans l'URL (?convention=), par exemple depuis le contrôle avant
  // transfert INSEE : elle est présélectionnée au lieu de la dernière
  const conventionDemandee = React.useMemo(
    () =>
      Number(new URLSearchParams(window.location.search).get('convention')) ||
      null,
    [],
  );

  // valeur stocké en local pour avoir les informations à jour
  const [baseline, setBaseline] = React.useState<Pacs>(pacsProps);
  const [registre, setRegistre] = React.useState<Partial<Registre> | null>(
    null,
  );

  React.useEffect(() => {
    if (!pacsProps?.libelleRegistre) return;

    const typeRegistre =
      pacsProps.derniereConvention?.statut === StatutPacs.PROJET ? 10 : null;

    request
      .get(getListeRegistres(pacsProps.libelleRegistre, typeRegistre))
      .then((data: unknown) => {
        const flat = flattenRegistres((data as any[]) ?? []);
        const found = flat.find(
          (r) => r.annee?.annee === pacsProps.anneeRegistre,
        );
        if (found) {
          setRegistre(found);
        }
      });
  }, [
    pacsProps?.libelleRegistre,
    pacsProps?.anneeRegistre,
    pacsProps?.derniereConvention?.statut,
  ]);

  const handleChange = React.useCallback((data: Partial<Pacs>) => {
    setPacs((prev) => {
      const next: Pacs = {
        ...prev,
        ...data,
        ...(data.derniereConvention && {
          derniereConvention: {
            ...prev?.derniereConvention,
            ...data.derniereConvention,
          },
        }),
      };

      // Garde conventionsDeclarations synchronisé avec derniereConvention
      if (data.derniereConvention && prev?.conventionsDeclarations?.length) {
        const changes = _.omit(data.derniereConvention, ['irrecevabilite']);
        const targetId = next.derniereConvention?.identifiant;
        const lastIndex = prev.conventionsDeclarations.length - 1;

        next.conventionsDeclarations = prev.conventionsDeclarations.map(
          (convention, index) => {
            const isTarget =
              targetId != null
                ? convention.identifiant === targetId
                : index === lastIndex;
            return isTarget ? { ...convention, ...changes } : convention;
          },
        );
      }

      return next;
    });
  }, []);

  // Répercute une convention déjà sauvegardée côté API dans la fiche
  const handleConventionSaved = React.useCallback((saved: any) => {
    const merge = (prev: Pacs): Pacs => {
      if (!prev) return prev;
      const isLast = (c: any) => c.identifiant === saved.identifiant;
      return {
        ...prev,
        conventionsDeclarations: prev.conventionsDeclarations?.map((c) =>
          isLast(c) ? { ...c, ...saved } : c,
        ),
        derniereConvention:
          prev.derniereConvention?.identifiant === saved.identifiant
            ? { ...prev.derniereConvention, ...saved }
            : prev.derniereConvention,
      };
    };
    setPacs(merge);
    setBaseline(merge);
  }, []);

  const handleSave = React.useCallback(async () => {
    return update?.(pacs).then((data) => {
      setPacs(data);
      setBaseline(data);
    });
  }, [pacs, update]);

  const handleRollback = React.useCallback(() => {
    if (!_.isNil(baseline)) {
      setPacs(baseline);
    }
  }, [baseline]);

  const updated = React.useMemo(
    () => !_.isEqual(pacs, baseline),
    [pacs, baseline],
  );

  const informationPanel = React.useMemo(
    () => (
      <Fiche.Panel
        key="informationPacs"
        id="informationsPacs"
        title="INFORMATIONS DU PACS"
        open
      >
        <InformationPacsPanel
          pacs={pacs}
          registre={registre}
          updated={updated}
          onChange={handleChange}
          onSave={handleSave}
          onRollback={handleRollback}
        />
      </Fiche.Panel>
    ),
    [pacs, registre, updated, handleChange, handleSave, handleRollback],
  );

  const conventionBase = React.useMemo(() => {
    const conventions = pacs?.conventionsDeclarations ?? [];
    return (
      conventions.find(
        (convention) => convention.identifiant === conventionDemandee,
      ) ?? conventions[conventions.length - 1]
    );
  }, [pacs?.conventionsDeclarations, conventionDemandee]);

  const informationDeclarationPanel = React.useMemo(
    () => (
      <Fiche.Panel
        key="informationDeclarationPacs"
        id="informationsDeclarationPacs"
        title="INFORMATIONS DES DÉCLARATIONS"
        open
      >
        <InformationsDeclarationsPacsPanel
          conventionsDeclarations={pacs?.conventionsDeclarations}
          derniereConventionStatut={pacs?.derniereConvention?.statut}
          derniereConventionEnregistre={
            pacs?.derniereConvention?.editionRegistre
          }
          derniereConventionConforme={pacs?.derniereConvention?.conforme}
          showDetails={pacs?.conventionsDeclarations?.length < 8}
          conventionDeclarationBase={conventionBase}
          pacs={pacs}
          onConventionSaved={handleConventionSaved}
        />
      </Fiche.Panel>
    ),
    [pacs, handleConventionSaved, conventionBase],
  );

  // Arrivée sur une convention précise : affichage direct du panneau des déclarations
  const panneauAffiche = React.useRef(false);
  React.useEffect(() => {
    if (
      conventionDemandee &&
      !panneauAffiche.current &&
      !_.isEmpty(pacs?.conventionsDeclarations)
    ) {
      panneauAffiche.current = true;
      document
        .getElementById('informationsDeclarationPacs')
        ?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
  }, [conventionDemandee, pacs?.conventionsDeclarations]);

  React.useEffect(() => {
    if (!_.isNil(pacsProps)) {
      setPacs(pacsProps);
      setBaseline(pacsProps);
    }
  }, [pacsProps]);

  return (
    <Fiche
      key="PacsFiche"
      type="pacs"
      header={`${formatNomClassement(
        pacs?.derniereConvention?.partenaire1NomClassement,
      )} et ${formatNomClassement(
        pacs?.derniereConvention?.partenaire2NomClassement,
      )}`}
      loading={loading}
    >
      {informationPanel}
      {informationDeclarationPanel}
    </Fiche>
  );
};

export default PacsFiche;
